import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { clsx } from '../../ds/clsx';
import { Banner, Button, Skeleton, useToast } from '../../ds/components';
import { useIsMobile } from '../../ds/hooks';
import { errorMessage } from '../../lib/api';
import { AvailabilityGrid } from '../calendar/AvailabilityGrid';
import { absoluteBookingUrl, bookingLinkText, colorBar, copyText } from '../calendar/EventTypesPage';
import { useAvailability, useEventTypes, useSaveAvailability, type EventType } from '../calendar/api';
import { sameDays, useAvailabilityForm } from '../calendar/availabilityForm';
import { SettingsPageHeader, useSavedPill } from './SettingsFrame';
import styles from './AvailabilitySettingsPage.module.css';

/** „45 Min · Puffer 15 Min · vor Ort" / „20 Min · kein Puffer" (Einstellungen 14). */
export function eventTypeSummaryLine(type: EventType): string {
  const buffer = Math.max(type.bufferBeforeMinutes, type.bufferAfterMinutes);
  const parts = [`${type.durationMinutes} Min`, buffer > 0 ? `Puffer ${buffer} Min` : 'kein Puffer'];
  if (type.channel === 'on_site' || type.channel === 'property_address') parts.push('vor Ort');
  return parts.join(' · ');
}

/** The public type whose link the page shares: the first active, bookable one. */
export function sharedEventType(types: readonly EventType[]): EventType | null {
  return types.find((type) => type.active && type.bookingUrl !== null) ?? null;
}

function EventTypesCard({ mobile }: { mobile: boolean }) {
  const navigate = useNavigate();
  const types = useEventTypes();
  return (
    <section className={clsx(styles.sideCard, mobile && styles.sideCardMobile)} aria-labelledby="types-title">
      <h2 id="types-title" className={styles.sideTitle}>
        Termintypen
      </h2>
      {types.isError ? <Banner tone="danger">{errorMessage(types.error)}</Banner> : null}
      {types.isPending ? (
        <div className={styles.typeList} aria-busy="true">
          <Skeleton height={44} />
          <Skeleton height={44} />
          <Skeleton height={44} />
        </div>
      ) : (
        <div className={clsx(styles.typeList, mobile && styles.typeListMobile)}>
          {(types.data ?? []).map((type) =>
            mobile ? (
              <Link key={type.id} to={`/calendar/event-types/${type.id}`} className={styles.typeRowMobile}>
                <span className={styles.dotSmall} style={{ background: colorBar(type) }} aria-hidden="true" />
                <span className={styles.typeNameMobile}>{type.name}</span>
                <span className={styles.typeDuration}>{type.durationMinutes} Min</span>
              </Link>
            ) : (
              <Link key={type.id} to={`/calendar/event-types/${type.id}`} className={styles.typeRow}>
                <span className={styles.dot} style={{ background: colorBar(type) }} aria-hidden="true" />
                <span className={styles.typeText}>
                  <span className={styles.typeName}>{type.name}</span>
                  <span className={styles.typeMeta}>{eventTypeSummaryLine(type)}</span>
                </span>
                <span className={styles.typeEdit}>Bearbeiten</span>
              </Link>
            ),
          )}
        </div>
      )}
      {!mobile ? (
        <button
          type="button"
          className={styles.addType}
          onClick={() => navigate('/calendar/event-types/new')}
        >
          + Termintyp anlegen
        </button>
      ) : null}
    </section>
  );
}

function BookingLinkCard({ mobile }: { mobile: boolean }) {
  const { toast } = useToast();
  const types = useEventTypes();
  const shared = sharedEventType(types.data ?? []);
  const url = shared?.bookingUrl ? absoluteBookingUrl(shared.bookingUrl) : null;

  async function share() {
    if (!url) return;
    if (mobile && typeof navigator.share === 'function') {
      try {
        await navigator.share(shared ? { url, title: shared.name } : { url });
        return;
      } catch {
        // fall through to copying (the sheet was dismissed or is unavailable)
      }
    }
    const ok = await copyText(url);
    toast({ message: ok ? 'Link kopiert.' : 'Der Link konnte nicht kopiert werden.' });
  }

  const linkText = shared?.bookingUrl ? bookingLinkText(shared.bookingUrl) : null;
  const rule = shared
    ? `Vorlaufzeit ${shared.minLeadTimeHours} Stunden · maximal ${shared.bookableUntilDays} Tage im Voraus`
    : null;

  if (mobile) {
    return (
      <div className={styles.linkRowMobile}>
        <span className={styles.linkText}>{linkText ?? 'Kein öffentlicher Termintyp'}</span>
        <Button
          variant="secondary"
          size="small"
          className={styles.shareButton}
          disabled={!url}
          onClick={() => void share()}
        >
          Teilen
        </Button>
      </div>
    );
  }
  return (
    <section className={styles.sideCard} aria-labelledby="link-title">
      <h2 id="link-title" className={styles.sideTitle}>
        Buchungslink
      </h2>
      <div className={styles.linkBox}>
        <span className={styles.linkText}>
          {types.isPending ? <Skeleton width={160} /> : (linkText ?? 'Kein öffentlicher Termintyp')}
        </span>
        <Button
          variant="secondary"
          size="small"
          className={styles.copyButton}
          disabled={!url}
          onClick={() => void share()}
        >
          Kopieren
        </Button>
      </div>
      {rule ? <span className={styles.rule}>{rule}</span> : null}
    </section>
  );
}

/** `/settings/availability` — Einstellungen 14 (EM6 on mobile): auto-saved week times, event types, booking link. */
export function AvailabilitySettingsPage() {
  const mobile = useIsMobile();
  const { markSaved } = useSavedPill();
  const availability = useAvailability();
  const save = useSaveAvailability();
  const form = useAvailabilityForm(availability.data);
  const [commitCount, setCommitCount] = useState(0);
  const lastSaved = useRef(form.days);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (availability.data) lastSaved.current = form.days;
    // Reset the baseline when fresh data arrives; `form.days` is re-initialised at the same time.
  }, [availability.data]);

  useEffect(() => {
    if (commitCount === 0) return;
    if (sameDays(form.days, lastSaved.current)) return;
    const body = form.prepare();
    if (!body) return;
    const snapshot = form.days;
    setSaveError(null);
    save
      .mutateAsync(body)
      .then(() => {
        lastSaved.current = snapshot;
        markSaved();
      })
      .catch((error: unknown) => {
        form.setServerError(error);
        setSaveError(errorMessage(error));
      });
    // Runs once per commit (toggle, blurred time field, closed day sheet).
  }, [commitCount]);

  const commit = () => setCommitCount((count) => count + 1);

  return (
    <div className={styles.page}>
      <SettingsPageHeader
        eyebrow="Meine Einstellungen"
        title="Verfügbarkeit und Termintypen"
        description="Innerhalb dieser Zeiten können Mandanten Termine über Ihren Buchungslink wählen."
      />
      {saveError ? <Banner tone="danger">{saveError}</Banner> : null}
      {availability.isError ? (
        <Banner
          tone="danger"
          actions={
            <Button variant="secondary" size="small" onClick={() => void availability.refetch()}>
              Erneut laden
            </Button>
          }
        >
          {errorMessage(availability.error)}
        </Banner>
      ) : null}
      <div className={clsx(styles.grid, mobile && styles.gridMobile)}>
        {mobile ? (
          availability.isPending ? (
            <div className={styles.skeleton} aria-busy="true">
              <Skeleton variant="block" height={340} />
            </div>
          ) : availability.data ? (
            <AvailabilityGrid form={form} variant="mobile" disabled={save.isPending} onCommit={commit} />
          ) : null
        ) : (
          <section className={styles.weekCard} aria-labelledby="week-title">
            <div className={styles.weekHead}>
              <h2 id="week-title" className={styles.weekTitle}>
                Wochenzeiten
              </h2>
              <span className={styles.weekZone}>Zeitzone Europa/Berlin</span>
            </div>
            {availability.isPending ? (
              <div className={styles.skeleton} aria-busy="true">
                {[0, 1, 2, 3, 4, 5, 6].map((index) => (
                  <Skeleton key={index} height={46} />
                ))}
              </div>
            ) : availability.data ? (
              <AvailabilityGrid form={form} variant="settings" disabled={save.isPending} onCommit={commit} />
            ) : null}
          </section>
        )}
        <div className={styles.side}>
          <EventTypesCard mobile={mobile} />
          <BookingLinkCard mobile={mobile} />
        </div>
      </div>
    </div>
  );
}
